<script setup lang="ts">
import { computed } from "vue";
import Icon from "./Icon.vue";
import type { BillingProfile, Progress, RunSummary, Server, SupportRequest, Invoice } from "../api";
import { go, href } from "../router";
import RunList from "./RunList.vue";
import ScanProgress from "./ScanProgress.vue";

const props = defineProps<{
  email: string;
  servers: Server[];
  runs: RunSummary[];
  invoices: Invoice[];
  support: SupportRequest[];
  live: { runId: string; progress: Progress | null } | null;
  billing: BillingProfile | null;
}>();
const emit = defineEmits<{ newScan: [] }>();

const hour = new Date().getHours();
const greeting = hour < 5 ? "Dobrej nocy" : hour < 12 ? "Dzień dobry" : hour < 18 ? "Dzień dobry" : "Dobry wieczór";

const finished = computed(() => props.runs.filter((r) => r.status !== "running"));
const removed = computed(() =>
  props.runs.reduce((n, r) => n + (r.progress?.actions.quarantined ?? r.quarantined) + (r.progress?.actions.repaired ?? r.repaired), 0),
);
const toConfirm = computed(() =>
  props.runs.filter((r) => r.mode === "quarantine" && r.status !== "running" && r.status !== "failed" && !r.feedback),
);
const unpaid = computed(() => props.invoices.filter((i) => i.status === "issued"));
const openSupport = computed(() => props.support.filter((s) => s.status !== "closed"));
const liveRun = computed(() => (props.live ? props.runs.find((r) => r.runId === props.live!.runId) ?? null : null));
const recent = computed(() => props.runs.slice(0, 5));
</script>

<template>
  <div class="page">
    <div class="hero">
      <div>
        <h1>{{ greeting }}</h1>
        <p class="muted">{{ email }} • {{ servers.length }} {{ servers.length === 1 ? "serwer" : "serwerów" }} pod opieką</p>
      </div>
      <button type="button" class="big" :disabled="!servers.length" @click="emit('newScan')"><Icon name="plus" :size="18" /> Nowa naprawa</button>
    </div>

    <!-- Wymaga uwagi — na samym szczycie, bo to są rzeczy, które klient MA zrobić. -->
    <div v-if="toConfirm.length || unpaid.length || openSupport.length || !billing" class="attention">
      <a v-if="!billing" class="att" :href="href('rozliczenia')">
        <span class="ic"><Icon name="receipt" :size="22" /></span>
        <span class="grow"><b>Uzupełnij dane do faktury</b><span>Potrzebne przed pierwszą płatną naprawą. Sprawdzenie strony działa bez nich.</span></span>
        <span class="go">Uzupełnij <Icon name="chevron-right" /></span>
      </a>
      <a v-for="r in toConfirm" :key="r.runId" class="att" :href="href('naprawy', r.runId)">
        <span class="ic"><Icon name="help" :size="22" /></span>
        <span class="grow"><b>Czy {{ r.domain }} działa po naprawie?</b><span>Potwierdź jednym kliknięciem albo poproś o pomoc.</span></span>
        <span class="go">Sprawdź <Icon name="chevron-right" /></span>
      </a>
      <a v-if="unpaid.length" class="att" :href="href('rozliczenia')">
        <span class="ic"><Icon name="receipt" :size="22" /></span>
        <span class="grow"><b>{{ unpaid.length }} {{ unpaid.length === 1 ? "faktura czeka" : "faktury czekają" }} na płatność</b><span>Szczegóły w zakładce Rozliczenia.</span></span>
        <span class="go">Zobacz <Icon name="chevron-right" /></span>
      </a>
      <a v-if="openSupport.length" class="att" :href="href('rozliczenia')">
        <span class="ic"><Icon name="briefcase" :size="22" /></span>
        <span class="grow"><b>{{ openSupport.length }} {{ openSupport.length === 1 ? "zgłoszenie" : "zgłoszenia" }} do devopsa w toku</b><span>Odezwiemy się na Twój adres e-mail.</span></span>
        <span class="go">Zobacz <Icon name="chevron-right" /></span>
      </a>
    </div>

    <div class="stats">
      <div class="stat"><div class="n">{{ servers.length }}</div><div class="l">serwery</div></div>
      <div class="stat"><div class="n">{{ finished.length }}</div><div class="l">wykonane naprawy</div></div>
      <div class="stat"><div class="n" :class="{ good: removed > 0 }">{{ removed }}</div><div class="l">usunięte zagrożenia</div></div>
      <div class="stat"><div class="n">{{ runs.filter((r) => r.status === "running").length }}</div><div class="l">w toku</div></div>
    </div>

    <div v-if="live" class="card live">
      <h2>Bieżąca naprawa <span class="spacer" /><a class="link" :href="href('naprawy', live.runId)">szczegóły <Icon name="chevron-right" /></a></h2>
      <div class="body">
        <p class="domain"><Icon name="globe" /> {{ liveRun?.domain ?? live.runId }}</p>
        <ScanProgress :progress="live.progress" :running="true" />
        <p v-if="!live.progress" class="muted">Uruchamiam…</p>
      </div>
    </div>

    <div v-if="!runs.length" class="card">
      <div class="body onboarding">
        <h3>Jak to działa</h3>
        <ol>
          <li><b>Dodaj serwer</b> w zakładce Serwery i kliknij <i>Testuj</i>, żeby sprawdzić dostęp.</li>
          <li><b>Uruchom naprawę</b>: wskażesz stronę i zdecydujesz, czy tylko sprawdzamy, czy też naprawiamy.</li>
          <li><b>Zobacz postęp na żywo</b>, a po zakończeniu potwierdź, że strona działa. Każdą zmianę możesz cofnąć.</li>
        </ol>
        <button v-if="!servers.length" type="button" @click="go('serwery')">Dodaj pierwszy serwer <Icon name="arrow-right" /></button>
        <button v-else type="button" @click="emit('newScan')"><Icon name="plus" /> Uruchom pierwszą naprawę</button>
      </div>
    </div>

    <template v-else>
      <div class="sechead">
        <h2>Ostatnie naprawy</h2>
        <a class="link" :href="href('naprawy')">wszystkie ({{ runs.length }}) <Icon name="chevron-right" /></a>
      </div>
      <RunList :runs="recent" compact />
    </template>
  </div>
</template>

<style scoped>
.hero { display: flex; align-items: center; justify-content: space-between; gap: var(--s4); flex-wrap: wrap; margin-bottom: var(--s5); }
.hero h1 { font-size: 24px; font-weight: 750; letter-spacing: -.02em; }
.hero p { margin: 4px 0 0; font-size: 13px; }
.attention { display: flex; flex-direction: column; gap: var(--s2); margin-bottom: var(--s5); }
.att {
  display: flex; align-items: center; gap: var(--s3); text-decoration: none; color: inherit;
  border: 1px solid var(--acc); background: var(--acc-dim); border-radius: var(--r2); padding: var(--s3) var(--s4);
}
.att:hover { filter: brightness(1.05); }
.att .ic { color: var(--acc); display: grid; place-items: center; }
.att b { display: block; font-size: 13px; }
.att span span { display: block; font-size: 12px; color: var(--mut); }
.att .go { color: var(--acc); font-weight: 600; font-size: 13px; white-space: nowrap; }
.stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: var(--s3); margin-bottom: var(--s5); }
@media (max-width: 720px) { .stats { grid-template-columns: repeat(2, 1fr); } }
.stat { background: var(--panel); border: 1px solid var(--bd); border-radius: var(--r3); padding: var(--s4); box-shadow: var(--shadow); }
.stat .n { font-size: 28px; font-weight: 750; letter-spacing: -.02em; line-height: 1.1; }
.stat .n.good { color: var(--ok); }
.stat .l { font-size: 12px; color: var(--mut); margin-top: 4px; }
.live { border-color: var(--acc); }
.domain { font-weight: 650; margin: 0 0 var(--s3); }
.onboarding h3 { font-size: 15px; margin-bottom: var(--s3); }
.onboarding ol { padding-left: var(--s5); margin: 0 0 var(--s4); line-height: 1.9; font-size: 13px; color: var(--mut); }
.onboarding b, .onboarding i { color: var(--fg); }
.sechead { display: flex; align-items: baseline; justify-content: space-between; margin: var(--s2) 0 var(--s3); }
.sechead h2 { font-size: 15px; font-weight: 700; }
</style>
